import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export function AuthDialog({ open, mode = "register", title, onClose, onSuccess }) {
  const { register, login } = useAuth();
  const [tab, setTab] = useState(mode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setTab(mode);
  }, [open, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let data;
      if (tab === "register") data = await register({ email, password, name });
      else data = await login({ email, password });
      toast.success(tab === "register" ? "Account created" : "Welcome back");
      onSuccess?.(data.user);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-[420px] rounded-xl p-0 overflow-hidden gap-0" data-testid="auth-dialog">
        <DialogTitle className="sr-only">{tab === "register" ? "Sign up" : "Log in"}</DialogTitle>
        <div className="p-6 sm:p-7">
          <div className="flex flex-col items-center text-center mb-5">
            <LogoMark size={44} />
            <h2 className="mt-3 text-[22px] font-extrabold tracking-[-0.01em] text-ink">
              {title || (tab === "register" ? "Save your names" : "Welcome back")}
            </h2>
            <p className="text-[13px] text-ink-muted mt-1">
              {tab === "register" ? "Create a free account to save & continue." : "Log in to your Namable account."}
            </p>
          </div>

          <div className="flex p-1 bg-canvas-alt rounded-full mb-5">
            {["register", "login"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`auth-tab-${t}`}
                className={`flex-1 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 ${
                  tab === t ? "bg-surface text-ink shadow-soft" : "text-ink-muted"
                }`}
              >
                {t === "register" ? "Sign up" : "Log in"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-3">
            {tab === "register" && (
              <Input
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 rounded-lg"
                data-testid="auth-name-input"
              />
            )}
            <Input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 rounded-lg"
              data-testid="auth-email-input"
            />
            <Input
              type="password"
              required
              minLength={6}
              placeholder="Password (min 6 chars)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 rounded-lg"
              data-testid="auth-password-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg bg-brand hover:bg-brand-dark text-white font-bold btn-glow"
              data-testid="auth-submit-btn"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : tab === "register" ? "Create account" : "Log in"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-1 bg-[color:var(--hairline-strong)]" />
            <span className="text-[11px] text-ink-subtle font-semibold uppercase tracking-wide">or</span>
            <div className="h-px flex-1 bg-[color:var(--hairline-strong)]" />
          </div>

          <Button
            variant="outline"
            onClick={googleLogin}
            className="w-full h-11 rounded-lg font-semibold border-[color:var(--hairline-strong)]"
            data-testid="auth-google-btn"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
            Continue with Google
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
